import { ClusterFeature } from 'supercluster'
import ClusteredGroup from './ClusteredGroup'
import IGeoJSONParser from './IGeoJSONParser'
import { BBOX, Cluster, GeoJSONPoint } from './types'
import IMapPointAdapter from './IMapPointAdapter'

export default class ClusterManager<T> {
    private clusteredGroup: ClusteredGroup<T>
    private parser: IGeoJSONParser<T>
    private map: IMapPointAdapter
    private clustersInBbox: Map<String, Array<Cluster<T>>>

    constructor(key: string, points: Array<T>, parser: IGeoJSONParser<T>, map: IMapPointAdapter) {
        this.parser = parser
        this.clusteredGroup = new ClusteredGroup<T>(key, points, parser)
        this.map = map
        this.clustersInBbox = new Map()
    }

    public cluster(key: string, points: Array<T>) {
        this.clusteredGroup.loadPointsToGroup(key, points)
    }

    public setClustersInBboxByClusteredGroup(key: string, zoom: number) {
        const clusterFeaturesForZoom = this.clusteredGroup.getIndexWithZoomByKey(key, zoom)
        const clusters: Array<Cluster<T>> = clusterFeaturesForZoom.map(feature =>
            this.parser.clusterFeatureToCluster(feature, zoom + 1)
        )

        const inBounds = clusters.reduce((total: Array<Cluster<T>>, current: Cluster<T>) => {
            if (this.map.findInBbox(current.marker)) {
                this.map.set(current.marker, current.clusterExpansionZoom, !!current.clusterId)
                return [...total, current]
            }
            return total
        }, [])

        this.clustersInBbox.set(key, inBounds)
    }

    public setClustersInBbox(zoom: number) {
        const clustersForZoom: Map<String, Array<ClusterFeature<any>>> = this.clusteredGroup.getIndexWithZoom(zoom)
        for (const [key, value] of clustersForZoom) {
            const clusters: Array<Cluster<T>> = value.map(v => this.parser.clusterFeatureToCluster(v, zoom + 1))

            const inBounds = clusters.reduce((total: Array<Cluster<T>>, current: Cluster<T>) => {
                if (this.map.findInBbox(current.marker)) {
                    this.map.set(current.marker, current.clusterExpansionZoom, !!current.clusterId)
                    return [...total, current]
                }
                return total
            }, [])
            this.clustersInBbox.set(key, inBounds)
        }
    }

    public hideClustersByClusteredGroup(key: string): void {
        const clusters: Array<Cluster<T>> = this.clustersInBbox.get(key)
        for (const cluster of clusters) {
            this.map.hide(cluster.marker)
        }
    }

    public hideClusters(): void {
        for (const [key, value] of this.clustersInBbox) {
            for (const cluster of value) {
                this.map.hide(cluster.marker)
            }
        }
    }

    public showClustersByClusteredGroup(key: string): void {
        const clusters: Array<Cluster<T>> = this.clustersInBbox.get(key)
        for (const cluster of clusters) {
            this.map.show(cluster.marker)
        }
    }

    public showClusters(): void {
        for (const [key, value] of this.clustersInBbox) {
            for (const cluster of value) {
                this.map.show(cluster.marker)
            }
        }
    }

    public deleteClustersByClusteredGroup(key: string): void {
        const clusters: Array<Cluster<T>> = this.clustersInBbox.get(key)
        for (const cluster of clusters) {
            this.map.delete(cluster.marker)
        }
        this.clustersInBbox.delete(key)
    }

    public deleteClusters(): void {
        for (const [key, value] of this.clustersInBbox) {
            for (const cluster of value) {
                this.map.delete(cluster.marker)
            }
        }

        this.clustersInBbox = new Map()
    }

    public clusterFiltered(key: string, points: Array<T>) {
        this.deleteClustersByClusteredGroup(key)
        this.clusteredGroup.filter(key, points)
        this.setClustersInBboxByClusteredGroup(key, this.map.getZoom())
    }

    public unfilter(key: string) {
        this.deleteClustersByClusteredGroup(key)
        this.clusteredGroup.unfilter(key)
        this.setClustersInBboxByClusteredGroup(key, this.map.getZoom())
    }
}
